/* eslint-disable no-console */
import debounce from 'lodash.debounce'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Box,
  Grid2,
  Typography,
  Divider,
  CircularProgress
} from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import {
  buyCelebrationPath,
  dashboardPath,
  verifyPath
} from 'components/Routes'
import SwitchVault from 'components/SwitchVault/SwitchVault'
import TradingPaused from 'components/TradingPaused/TradingPaused'
import TransactionButtons from 'components/TransactionButtons/TransactionButtons'
import {
  DEBOUNCE_INTERVAL,
  REVIEW_ORDER_EXPIRED,
  TRADING_IS_PAUSED
} from 'constants/index'
import { useFormik } from 'formik'
import { Vault } from 'api/inventory'
import { ReviewOrder } from 'api/trading'
import { localProductsData } from 'hooks/useProducts'
import useProducts from 'hooks/useProducts'
import {
  useMutateUpdateReviewOrderQuantity,
  useMutateUpdateReviewOrderVault,
  useMutateCreateMarketTransaction
} from 'hooks/useTrading'
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { roundUpDollarsAndCents } from 'utils/util'
import * as Yup from 'yup'
import FormattedInputs from '../MyVault/ReviewSell/InputNumber'
import useStyles from './styles'
import theme from 'utils/theme'
import DefaultLayout from 'components/layout/DefaultLayout'

export interface Props {
  reviewOrderData?: ReviewOrder
  reviewOrderId: string
  reviewOrderLoading?: boolean
  productId: number
}

export const FEE_PLAN_TYPES: { [key: string]: string } = {
  INSURANCE: 'Standard Insurance Fee',
  TRANSACTION: 'Standard Transaction Fee'
}

interface FormValues {
  amount: string
  weight: number
  spotPrice?: number
}

const validationSchema = Yup.object().shape({
  amount: Yup.string(),
  weight: Yup.number()
    .required('Quantity is required')
    .min(0.001, 'Minimum is 0.001g')
})

export default function ReviewPurchase({
  reviewOrderData,
  reviewOrderId,
  reviewOrderLoading,
  productId
}: Readonly<Props>): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { user, setToast, refreshCurrentUser } = useAuth()

  const { products } = useProducts()
  const product = products.find(p => p.id === productId)
  const unitType = product?.unitType ?? 'mg'

  // States
  const [tradingPaused, setTradingPaused] = useState<boolean>(false)
  const [helperTextAmountInput, setHelperTextAmountInput] = useState<string>('')
  const [disabledButton, setDisabledButton] = useState<boolean>(true)
  const [updatedOrderData, setUpdatedOrderData] = useState<ReviewOrder | null>(
    null
  )

  const fees = updatedOrderData?.fees ?? reviewOrderData?.fees
  // totalPrice = total with fees; derive without-fees amount for form
  const totalPriceWithFees =
    updatedOrderData?.totalPrice ?? reviewOrderData?.totalPrice
  const totalFeeAmount =
    updatedOrderData?.totalFees ?? reviewOrderData?.totalFees
  const totalPriceWithoutFees =
    totalPriceWithFees != null && totalFeeAmount != null
      ? Number(totalPriceWithFees) - Number(totalFeeAmount)
      : undefined
  const initQuantityMg = reviewOrderData?.quantityMg
  const pricePerUnit = reviewOrderData?.pricePerUnit

  const [reviewOrderWeight, setReviewOrderWeight] = useState<number | null>(
    null
  )
  const [weight, setWeight] = useState<number | null>(null)

  useEffect(() => {
    // initQuantityMg is in mg; display as grams
    if (initQuantityMg != null) {
      const weightNumber = Number(initQuantityMg) / 1000
      setWeight(weightNumber)
      setReviewOrderWeight(weightNumber)
    }
  }, [initQuantityMg])

  const fetchLocalProductData = () => {
    if (product?.name) {
      return localProductsData[product.name.toLowerCase()]
    } else {
      return {
        shortDescription: '',
        image: '',
        descriptionParagraphs: '',
        termsAcceptanceLink: ''
      }
    }
  }

  // Spendable funds = cash + unexpired credits, matching the backend's own gate
  // (services/trading.py: `(buyer_balance + expirable) < total_price` refuses the
  // trade). Reading cash alone made this screen STRICTER than the API: a customer
  // holding voucher or reward credit but little cash was told to top up the full
  // order and had Complete Trade disabled, even though the backend would have
  // settled from their credit. Rails included credits in `balance` by definition
  // (FetchWalletBalance: `@balance += @expirable_wallet_credit_balance`), so this
  // restores the pre-migration behaviour as well as matching the gate.
  //
  // NOT clamped at zero: a negative balance is genuine arrears (Rails has an
  // Arrears report built on `WalletBalance.where('balance < 0')`) and must still
  // be collected at checkout.
  const walletBalanceOfUser = useMemo(() => {
    const cash = Number(user?.wallet?.balance ?? 0)
    const credits = Number(user?.wallet?.credits ?? 0)
    return (
      (Number.isFinite(cash) ? cash : 0) +
      (Number.isFinite(credits) ? credits : 0)
    )
  }, [user?.wallet?.balance, user?.wallet?.credits])

  // Never let a missing or malformed field become NaN: NaN loses every comparison,
  // so it does not surface as a wrong number, it makes whole rows disappear.
  const money = (value: unknown): number => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  // Accept either spelling. Rails' GraphQL camelized these; the REST payload now
  // sends both, but a customer on a cached bundle may still be talking to an older
  // shape, and 0 is the safe reading of "not told".
  const rewardCredit = money(
    user?.wallet?.rewardCredit ?? user?.wallet?.reward_credit
  )
  const voucherCredit = money(
    user?.wallet?.voucherCredit ?? user?.wallet?.voucher_credit
  )

  const goBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const updateQuantityMutation = useMutateUpdateReviewOrderQuantity()
  const updateVaultMutation = useMutateUpdateReviewOrderVault()
  const createTransactionMutation = useMutateCreateMarketTransaction()

  const [updateReviewOrderWithVault, { loading }] = [
    (id: number | null) =>
      new Promise<Vault>((resolve, reject) => {
        updateVaultMutation.mutate(
          { id: Number(reviewOrderId), vaultId: id as number },
          {
            onSuccess: data => {
              setUpdatedOrderData(data)
              resolve(data.vault as unknown as Vault)
            },
            onError: reject
          }
        )
      }),
    { loading: updateVaultMutation.isLoading }
  ] as const

  const creatingMarketTransaction = createTransactionMutation.isLoading

  // useForm hook
  const {
    values,
    errors,
    touched,
    setTouched,
    setErrors,
    setFieldValue,
    handleChange
  } = useFormik({
    initialValues: {
      amount: totalPriceWithoutFees
        ? Number(totalPriceWithoutFees).toFixed(2)
        : '',
      weight: initQuantityMg ? Number(initQuantityMg) / 1000 : 0
    },
    initialTouched: {
      amount: false,
      weight: false
    },
    validationSchema,
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    onSubmit: (values: FormValues) => {},
    enableReinitialize: true
  })

  const updateReviewOrderWeight = useCallback(
    (
      weight: number,
      existingAmount: number,
      touched: { amount: boolean; weight: boolean }
    ) => {
      console.log('updateReviewOrderWeight', weight)

      updateQuantityMutation.mutate(
        {
          id: Number(reviewOrderId),
          quantityMg: Math.round(weight * 1000)
        },
        {
          onError: (error: unknown) => {
            const err = error as { message?: string }
            setErrors({
              weight: err?.message ?? 'Error updating',
              amount: undefined
            })
          },
          onSuccess: data => {
            setUpdatedOrderData(data)
            const updatedTotalWithFees = data.totalPrice
            const updatedTotalFees = data.totalFees
            const updatedWithoutFees =
              updatedTotalWithFees != null && updatedTotalFees != null
                ? Number(updatedTotalWithFees) - Number(updatedTotalFees)
                : undefined

            if (
              updatedWithoutFees != null &&
              Number(Number(existingAmount).toFixed(2)) !==
                Number(Number(updatedWithoutFees).toFixed(2))
            ) {
              setHelperTextAmountInput(
                `We've rounded to $${roundUpDollarsAndCents(
                  Number(updatedWithoutFees)
                ).toString()}`
              )
            }

            setReviewOrderWeight(
              data.quantityMg ? Number(data.quantityMg) / 1000 : null
            )

            setFieldValue(
              'amount',
              updatedWithoutFees != null
                ? roundUpDollarsAndCents(Number(updatedWithoutFees)).toString()
                : '0'
            )

            setTouched(touched)
            setErrors({ weight: undefined, amount: undefined })
          }
        }
      )
    },
    [
      reviewOrderId,
      setErrors,
      setFieldValue,
      setTouched,
      updateQuantityMutation
    ]
  )

  const shortDebouncedWeightUpdate = useMemo(
    () =>
      debounce(
        (
          weight: number,
          existingAmount: number,
          touched: { amount: boolean; weight: boolean }
        ) => {
          updateReviewOrderWeight(weight, existingAmount, touched)
        },
        DEBOUNCE_INTERVAL
      ),
    [updateReviewOrderWeight]
  )

  // render total / fees / price
  const total = useMemo(() => {
    return totalPriceWithFees ?? 0
  }, [totalPriceWithFees])

  const topUpPrice = useMemo(() => {
    const price = roundUpDollarsAndCents(Number(total) - walletBalanceOfUser)
    return price < 1 ? 1 : price
  }, [total, walletBalanceOfUser])

  const isShowTopUp = useMemo(() => {
    const diff = roundUpDollarsAndCents(Number(total) - walletBalanceOfUser)
    setDisabledButton(diff > 0)
    return diff > 0
  }, [total, walletBalanceOfUser])

  const transactionFee = useMemo(() => {
    return Number(
      fees?.find(fee => fee.label === FEE_PLAN_TYPES.TRANSACTION)?.amount ?? 0
    ).toFixed(2)
  }, [fees])

  const handleConfirm = async () => {
    if (!disabledButton) {
      const paymentForm = document.getElementById(
        'credit-or-debit-form'
      ) as HTMLFormElement
      if (paymentForm) {
        paymentForm.requestSubmit()
      } else {
        localStorage.removeItem('url_after_POLi_transfer')
        createTransactionMutation.mutate(parseInt(reviewOrderId), {
          onSuccess: async () => {
            setDisabledButton(false)
            await refreshCurrentUser()
            setToast({
              open: true,
              message: `Good as ${product?.name}! Market transaction created.`,
              type: 'success',
              duration: 3000
            })
            setTradingPaused(false)
            navigate(
              buyCelebrationPath({
                routeParams: { productId: product?.id }
              }),
              {
                state: {
                  weight: Number(weight),
                  unitType,
                  productId: product?.id
                }
              }
            )
          },
          onError: (error: unknown) => {
            setDisabledButton(false)
            const err = error as { message?: string; errorStatus?: string }
            if (err?.errorStatus === TRADING_IS_PAUSED) {
              setTradingPaused(true)
            }
            setToast({
              open: true,
              message: err?.message ?? 'An error occurred',
              type: 'error',
              duration: 3000
            })
            if (err?.errorStatus === REVIEW_ORDER_EXPIRED) {
              navigate(dashboardPath())
            }
          }
        })
      }
    }
  }

  // change vault function
  const handleChangeVault = async (id: number | null): Promise<Vault> => {
    return updateReviewOrderWithVault(id)
  }

  // Quantity (in product unit_type) purchasable for a given dollar amount.
  const calculateDecigramsRoundedDown = useCallback(
    (amount: number) =>
      amount > 0 && Number(pricePerUnit) > 0
        ? Math.max(Math.floor(amount / Number(pricePerUnit)) / 1000, 0)
        : 0,
    [pricePerUnit]
  )

  const handleBlurInputs = (e: FocusEvent<HTMLInputElement>) => {
    console.log('handleBlurInputs', e)

    if (reviewOrderWeight !== Number(values.weight)) {
      shortDebouncedWeightUpdate.cancel()
      updateReviewOrderWeight(Number(values.weight), Number(values.amount), {
        amount: false,
        weight: false
      })
    }
  }

  const handleChangeWeightInput = (e: ChangeEvent<HTMLInputElement>) => {
    console.log('handleChangeWeightInput', e)

    setWeight(Number(e.target.value))

    if (touched.weight) {
      handleChange(e)
      const amount = Number(e.target.value) * Number(pricePerUnit) * 1000
      setFieldValue('amount', amount.toFixed(2))
      shortDebouncedWeightUpdate(Number(e.target.value), amount, {
        amount: false,
        weight: true
      })
    } else {
      handleChange(e)
    }
  }

  const handleChangeAmountInput = (e: ChangeEvent<HTMLInputElement>) => {
    console.log('handleChangeAmountInput', e)

    if (touched.amount) {
      handleChange(e)
      const weight = calculateDecigramsRoundedDown(Number(e.target.value))
      setFieldValue('weight', weight)
      shortDebouncedWeightUpdate(weight, Number(e.target.value), {
        amount: true,
        weight: false
      })
    } else {
      handleChange(e)
    }
  }

  const handleIncreaseWeight = useCallback(() => {
    if (!loading) {
      const newWeight = Number(values.weight) + 0.001

      setTouched({ amount: false, weight: true })
      setFieldValue('weight', newWeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePerUnit, values.amount, loading])

  const handleDecreaseWeight = useCallback(() => {
    if (!loading) {
      const newWeight = Math.max(Number(values.weight) - 0.001, 0.001)

      setTouched({ amount: false, weight: true })
      setFieldValue('weight', newWeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePerUnit, values.amount, loading])

  const nextStepButtons = () => {
    const showIsNotVerified =
      user &&
      !user?.profile?.onboarded &&
      !user?.profile?.partialIdentityVerified

    if (showIsNotVerified) {
      return (
        <Grid2 mt={5}>
          <Button
            fullWidth
            variant='outlined'
            type='submit'
            onClick={() => {
              navigate(verifyPath())
            }}
          >
            Verify and buy
          </Button>
        </Grid2>
      )
    } else if (isShowTopUp) {
      return (
        <Grid2>
          <Divider className={classes.divider} />
          <Grid2 container mt={2}>
            <Typography>TOP UP AMOUNT</Typography>
            <Box textAlign='right'>
              <Typography>${Number(topUpPrice).toFixed(2)}</Typography>
            </Box>
          </Grid2>
          <TransactionButtons
            price={topUpPrice}
            setDisabledButton={setDisabledButton}
            handlePaymentSuccess={() =>
              createTransactionMutation.mutate(parseInt(reviewOrderId), {
                onSuccess: async () => {
                  await refreshCurrentUser()
                }
              })
            }
            loading={creatingMarketTransaction}
            linkToRecharge
            hideBankTransfer
            paymentParams={{
              amount: topUpPrice.toString(),
              payment_type: 'top_up_to_wallet'
            }}
          />
          <Grid2>
            <Button
              disabled={disabledButton}
              fullWidth
              type='button'
              id='buy-confirmation-button'
              onClick={handleConfirm}
              style={{ marginBottom: '2rem' }}
            >
              Complete Trade
            </Button>
          </Grid2>
        </Grid2>
      )
    } else if (!disabledButton) {
      return (
        <Grid2 mt={5}>
          <Button fullWidth type='submit' onClick={handleConfirm}>
            Complete Trade
          </Button>
        </Grid2>
      )
    } else {
      return <></>
    }
  }

  if (tradingPaused) {
    return <TradingPaused />
  }

  // How much of the order the existing balance covers. This is the SAME quantity
  // the "you'll need to pay" figure below subtracts, so the itemised rows always
  // account for exactly the gap between TOTAL and YOU'LL NEED TO PAY.
  //
  // Greg, 2026-08-03: TOTAL $102.30 above YOU'LL NEED TO PAY $97.30, with nothing
  // explaining the $5. The rows were driven by `wallet.rewardCredit` /
  // `wallet.voucherCredit`, which the serverless /user/me did not send — so they
  // were Number(undefined) = NaN. Every comparison against NaN is false, so all
  // three rows took the hidden branch while the pay figure, computed separately
  // from `balance + credits`, still deducted the money.
  const amountCoveredByBalance = Math.min(
    Math.max(walletBalanceOfUser, 0),
    total
  )

  // Rails' order: reward credit, then voucher credit, then cash.
  const totalRewardCreditToUse = Math.min(rewardCredit, amountCoveredByBalance)
  const totalVoucherCreditToUse = Math.min(
    voucherCredit,
    amountCoveredByBalance - totalRewardCreditToUse
  )
  // Derived as the remainder rather than read independently. Whatever the credit
  // buckets did not cover is coming from cash, so the three rows reconcile even if
  // a credit field goes missing again or a credit_type appears that the API cannot
  // attribute to either bucket — the exact failure that produced this bug.
  const totalWalletBalanceToUse =
    amountCoveredByBalance - totalRewardCreditToUse - totalVoucherCreditToUse

  const displayLoading =
    reviewOrderWeight !== null && Number(weight) !== Number(reviewOrderWeight)

  return (
    <DefaultLayout
      pageTitle='Checkout'
      loading={reviewOrderLoading || creatingMarketTransaction}
      loadingAnimation
    >
      <Grid2 px={2} className={classes.root}>
        <Grid2 mt={2} container>
          <ArrowBackIcon fontSize='large' onClick={goBack} />
        </Grid2>

        <Grid2 container>
          <Typography variant='body2'>SELECT VAULT</Typography>
          <SwitchVault
            handleActive={handleChangeVault}
            loading={loading}
            text=''
          />
        </Grid2>

        <Grid2 container>
          <Grid2 size={{ xs: 6 }}>
            <Typography textAlign='left'>ASSET</Typography>
          </Grid2>
          <Grid2 size={{ xs: 3 }}>
            <Typography textAlign='right'>AMOUNT</Typography>
          </Grid2>
          <Grid2 size={{ xs: 3 }}>
            <Typography textAlign='right'>TOTAL</Typography>
          </Grid2>
        </Grid2>
        <Divider className={classes.divider} />
        <Grid2 container>
          <Grid2 size={{ xs: 6 }}>
            <img
              src={fetchLocalProductData()?.image}
              alt='{product.name}-bullion'
              className={classes.productImage}
            />
            <Box>
              <Typography textTransform='uppercase'>{product?.name}</Typography>
              <Typography textTransform='uppercase' variant='body2'>
                {fetchLocalProductData()?.shortDescription}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 3 }}>
            <Box onClick={handleIncreaseWeight} textAlign='right'>
              <KeyboardArrowUpIcon fontSize='large' />
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              justifyContent='center'
              alignItems='flex-end'
            >
              <Box textAlign='right'>
                <FormattedInputs
                  name='weight'
                  value={values.weight}
                  unitType={unitType}
                  onChange={handleChangeWeightInput}
                  onBlur={handleBlurInputs}
                  onFocus={() => setTouched({ amount: false, weight: true })}
                  sx={{
                    '& input': {
                      color: 'text.primary',
                      textAlign: 'right',
                      bgcolor: 'transparent',
                      p: 0,
                      textDecoration: 'underline',
                      textDecorationColor: theme.palette.primary.main,
                      textUnderlineOffset: '4px'
                    }
                  }}
                />
              </Box>
            </Box>
            <Box onClick={handleDecreaseWeight} textAlign='right'>
              <KeyboardArrowDownIcon fontSize='large' />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 3 }}>
            <Box textAlign='right'>
              <FormattedInputs
                name='amount'
                value={values.amount}
                onChange={handleChangeAmountInput}
                onBlur={handleBlurInputs}
                onFocus={() => setTouched({ amount: true, weight: false })}
                sx={{
                  '& input': {
                    color: 'text.primary',
                    textAlign: 'right',
                    bgcolor: 'transparent',
                    p: 0,
                    textDecoration: 'underline',
                    textDecorationColor: theme.palette.primary.main,
                    textUnderlineOffset: '4px'
                  }
                }}
              />
            </Box>
          </Grid2>
        </Grid2>

        {errors && errors.weight && (
          <Grid2>
            <Typography align='right' variant='body2' color='primary'>
              {errors.weight}
            </Typography>
          </Grid2>
        )}

        {helperTextAmountInput && (
          <Grid2>
            <Typography align='right' variant='body2' color='primary'>
              {helperTextAmountInput}
            </Typography>
          </Grid2>
        )}

        <Divider className={classes.divider} />
        <Box display='flex' justifyContent='center' alignItems='center'>
          {displayLoading && <CircularProgress color='default' size={120} />}
        </Box>
        <Box visibility={displayLoading ? 'hidden' : 'visible'}>
          {totalRewardCreditToUse > 0 && (
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              my={2}
            >
              <Box>
                <Typography textTransform='uppercase'>Reward Credit</Typography>
                <Typography variant='body2'>
                  Use reward credit from your wallet
                </Typography>
              </Box>
              <Typography>
                -${Number(totalRewardCreditToUse).toFixed(2)}
              </Typography>
            </Box>
          )}

          {totalVoucherCreditToUse > 0 && (
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              my={2}
            >
              <Box>
                <Typography textTransform='uppercase'>
                  Voucher Credit
                </Typography>
                <Typography variant='body2'>
                  Use voucher credit from your wallet
                </Typography>
              </Box>
              <Typography>
                -${Number(totalVoucherCreditToUse).toFixed(2)}
              </Typography>
            </Box>
          )}

          {totalWalletBalanceToUse > 0 && (
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              my={2}
            >
              <Box>
                <Typography textTransform='uppercase'>Wallet</Typography>
                <Typography variant='body2'>
                  Use ${Number(totalWalletBalanceToUse).toFixed(2)}
                </Typography>
              </Box>
              <Typography>-${totalWalletBalanceToUse.toFixed(2)}</Typography>
            </Box>
          )}

          {/*
            Arrears is stated under "You'll need to pay", not as a row here. The
            block that used to sit at this spot keyed off `totalWalletBalanceToUse
            < 0`, which can no longer happen — that value is now a share of
            `amountCoveredByBalance`, which is floored at zero. An owing balance
            INCREASES what is due, so it never belonged among the rows that
            reduce it.
          */}

          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mt={4}
          >
            <Box>
              <Typography>TOTAL</Typography>
              <Box display='flex' gap={2}>
                <Typography variant='body2'>
                  Includes transaction fee of 0.89% + $1.5: +${transactionFee}
                </Typography>
              </Box>
            </Box>
            <Typography>${Number(total ?? 0).toFixed(2)}</Typography>
          </Box>

          <Divider className={classes.divider} />

          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mt={4}
          >
            <Box>
              <Typography variant='h6' textTransform='uppercase'>
                You'll need to pay
              </Typography>
              {/*
                When the wallet is in arrears this figure is HIGHER than the
                order total, because it collects the order plus the outstanding
                balance. That is correct — Rails tracked the same debt and
                reports on it (Arrears report) — but with nothing explaining it
                the screen reads as broken arithmetic. Cam reported exactly that
                on 2026-08-03: "says I need to pay more than the total".
              */}
              {walletBalanceOfUser < 0 && (
                <Typography variant='body2' color='text.secondary'>
                  Includes ${Math.abs(walletBalanceOfUser).toFixed(2)}{' '}
                  outstanding on your account
                </Typography>
              )}
            </Box>
            <Typography variant='h6'>
              ${Number(Math.max(total - walletBalanceOfUser, 0)).toFixed(2)}
            </Typography>
          </Box>

          {nextStepButtons()}
        </Box>
      </Grid2>
    </DefaultLayout>
  )
}
